import React, { useState, useEffect } from 'react';
import { createInvite, fetchInvites, fetchUsers, generateResetPasswordLink, toggleUserActive, fetchCurrentUser, refreshAccessToken, updateUser } from '../api/usermanagement';
import { FaChevronDown, FaChevronUp, FaCopy, FaEdit, FaPlus, FaKey, FaUndo } from 'react-icons/fa';
import { FiSave } from 'react-icons/fi';

function UserManagement({ token, setShowLoginModal, setToken }) {
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [resetPopup, setResetPopup] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvitesOpen, setIsInvitesOpen] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    is_admin: false,
    is_active: true,
    idea_limit: null,
    storage_limit_mb: null,
    ai_message_limit: null,
    creation_ai_limit: null,
    chat_ai_limit: null,
    project_ai_limit: null,
    enforce_ai_sublimits: false,
  });

  useEffect(() => {
    const currentToken = localStorage.getItem('access_token');
    if (currentToken) {
      checkAdminStatus();
    } else {
      setShowLoginModal(true);
      setIsLoading(false);
    }
  }, [setShowLoginModal]);

  const checkAdminStatus = async () => {
    let currentToken = localStorage.getItem('access_token');
    if (!currentToken) {
      setShowLoginModal(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetchCurrentUser(currentToken);

      if (response && typeof response.is_admin === 'boolean') {
        setIsAdmin(response.is_admin);
        if (response.is_admin) {
          await Promise.all([fetchPendingInvites(currentToken), fetchAllUsers(currentToken)]);
        } else {
          setError('Access denied: Admin privileges required');
        }
      } else {
        console.error('Invalid response format:', response);
        setError('Invalid user data: is_admin field missing');
      }
      setIsLoading(false);
    } catch (err) {
      console.error('fetchCurrentUser error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 401) {
        console.log('401 error detected, attempting token refresh');
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) {
            console.log('No refresh token available');
            setShowLoginModal(true);
            setIsLoading(false);
            return;
          }
          console.log('Refreshing token with:', refreshToken.substring(0, 20) + '...');
          const refreshResponse = await refreshAccessToken(refreshToken);
          console.log('refreshAccessToken response:', refreshResponse);

          if (refreshResponse.access_token) {
            localStorage.setItem('access_token', refreshResponse.access_token);
            localStorage.setItem('refresh_token', refreshResponse.refresh_token);
            setToken(refreshResponse.access_token);
            currentToken = refreshResponse.access_token;

            console.log('Retrying fetchCurrentUser with new token');
            const newResponse = await fetchCurrentUser(currentToken);
            console.log('fetchCurrentUser after refresh:', newResponse);

            if (newResponse && typeof newResponse.is_admin === 'boolean') {
              setIsAdmin(newResponse.is_admin);
              if (newResponse.is_admin) {
                console.log('User is admin after refresh, fetching data');
                await Promise.all([fetchPendingInvites(currentToken), fetchAllUsers(currentToken)]);
              } else {
                console.log('User is not admin after refresh');
                setError('Access denied: Admin privileges required');
              }
            } else {
              console.error('Invalid response after refresh:', newResponse);
              setError('Invalid user data after refresh');
            }
          } else {
            throw new Error('No access token in refresh response');
          }
        } catch (refreshErr) {
          console.error('Token refresh failed:', {
            message: refreshErr.message,
            status: refreshErr.response?.status,
            data: refreshErr.response?.data,
          });
          setShowLoginModal(true);
          setError('Failed to refresh token, please log in again');
        }
      } else if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else if (err.response?.status === 404) {
        setError('User endpoint not found, please check backend configuration');
      } else {
        setError('Failed to verify user: ' + (err.response?.data?.detail || err.message));
      }
      setIsLoading(false);
    }
  };

  const fetchPendingInvites = async (currentToken) => {
    try {
      const response = await fetchInvites(currentToken);
      console.log('Fetched invites:', response);
      setInvites(Array.isArray(response) ? response : []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch invites:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to fetch invites: ' + (err.response?.data?.detail || err.message));
      }
      setInvites([]);
    }
  };

  const fetchAllUsers = async (currentToken) => {
    try {
      const response = await fetchUsers(currentToken);
      console.log('Fetched users:', response);
      setUsers(Array.isArray(response) ? response : []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch users:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to fetch users: ' + (err.response?.data?.detail || err.message));
      }
      setUsers([]);
    }
  };

  const handleCreateInvite = async () => {
    const currentToken = localStorage.getItem('access_token');
    try {
      const response = await createInvite(currentToken);
      console.log('Created invite:', response);
      if (response.invite_url) {
        setInviteLink(response.invite_url);
        setShowPopup(true);
        await fetchPendingInvites(currentToken);
        setError('');
      } else {
        setError('Invite creation failed: No invite URL returned');
        alert('Failed to create invite: No invite URL returned');
      }
    } catch (err) {
      console.error('Create invite error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to create invite: ' + (err.response?.data?.detail || err.message));
        alert('Failed to create invite: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleResetPassword = async (userId) => {
    const currentToken = localStorage.getItem('access_token');
    try {
      const response = await generateResetPasswordLink(userId, currentToken);
      console.log('Reset password response:', response);
      setResetLink(response.reset_url);
      setResetPopup(true);
      setError('');
    } catch (err) {
      console.error('Reset password error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to generate reset link: ' + (err.response?.data?.detail || err.message));
        alert('Failed to generate reset link: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleResetAIUsage = async (userId) => {
    const currentToken = localStorage.getItem('access_token');
    try {
      const response = await fetch(`http://localhost:8000/api/users/${userId}/reset-ai-usage`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      console.log('Reset AI usage for user:', userId);
      await fetchAllUsers(currentToken);
      setError('');
      alert('AI usage reset successfully');
    } catch (err) {
      console.error('Reset AI usage error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to reset AI usage: ' + (err.response?.data?.detail || err.message));
        alert('Failed to reset AI usage: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      is_admin: user.is_admin,
      is_active: user.is_active,
      idea_limit: user.idea_limit,
      storage_limit_mb: user.storage_limit_mb,
      ai_message_limit: user.ai_message_limit,
      creation_ai_limit: user.creation_ai_limit,
      chat_ai_limit: user.chat_ai_limit,
      project_ai_limit: user.project_ai_limit,
      enforce_ai_sublimits: user.enforce_ai_sublimits,
    });
  };

  const handleUpdateUser = async () => {
    const currentToken = localStorage.getItem('access_token');
    try {
      await updateUser(editingUser.id, userForm, currentToken);
      console.log('Updated user:', editingUser.id);
      await fetchAllUsers(currentToken);
      setEditingUser(null);
      setError('');
      alert('User settings updated successfully');
    } catch (err) {
      console.error('Update user error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      if (err.response?.status === 403) {
        setError('Access denied: Admin privileges required');
      } else {
        setError('Failed to update user: ' + (err.response?.data?.detail || err.message));
        alert('Failed to update user: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const copyToClipboard = async (text, type) => {
    if (!text) {
      alert(`No ${type} to copy!`);
      setError(`No ${type} to copy!`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      alert(`${type} copied to clipboard!`);
      setError('');
    } catch (err) {
      console.error(`Copy ${type} failed:`, { message: err.message });
      alert(`Failed to copy ${type}: ` + err.message);
      setError(`Failed to copy ${type}: ` + err.message);
    }
  };

  const validInvites = invites.filter(
    (invite) => !invite.used_by && new Date(invite.expires_at) >= new Date()
  );
  const expiredInvites = invites
    .filter((invite) => invite.used_by || new Date(invite.expires_at) < new Date())
    .sort((a, b) => new Date(b.expires_at) - new Date(a.expires_at))
    .slice(0, 5);
  const displayedInvites = [...validInvites, ...expiredInvites];

  if (isLoading) {
    return (
      <div className="flex h-full bg-[var(--bg-primary)]">
        <div className="flex-1 p-6 overflow-y-auto">
          <p className="text-[var(--text-secondary)] text-center">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex h-full bg-[var(--bg-primary)]">
        <div className="flex-1 p-6 overflow-y-auto">
          <p className="text-danger text-center">
            Access denied: Admin privileges required
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[var(--bg-primary)] overflow-hidden">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-[var(--primary-light)] to-[var(--primary)] px-4 py-4 flex items-center justify-between">
        <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)]">User Management</h2>
        <button
          className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition text-sm sm:text-base"
          onClick={handleCreateInvite}
        >
          <FaPlus className="sm:hidden" />
          <span className="hidden sm:inline">Create Invite</span>
          <span className="sm:hidden">Invite</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-8 overflow-y-auto">
        {error && (
          <div className="bg-danger-light border text-danger px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Invites Section */}
        <div className="mb-6">
          <button
            className="flex items-center gap-2 w-full text-lg font-semibold text-[var(--text-primary)] mb-3 focus:outline-none"
            onClick={() => setIsInvitesOpen(!isInvitesOpen)}
          >
            {isInvitesOpen ? <FaChevronUp /> : <FaChevronDown />}
            Invites
          </button>
          {isInvitesOpen && (
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg">
              {displayedInvites.length > 0 ? (
                <ul className="space-y-3">
                  {displayedInvites.map((invite) => (
                    <li
                      key={invite.id}
                      className="text-sm text-[var(--text-primary)] flex justify-between items-center"
                    >
                      <div className="flex flex-col">
                        <a
                          href={invite.invite_url}
                          className="underline text-[var(--primary-light)] hover:text-[var(--primary)]"
                        >
                          {invite.invite_url || 'No URL'}
                        </a>
                        <span className="text-[var(--text-secondary)]">
                          Status:{' '}
                          {invite.used_by
                            ? `Used by ${invite.used_by.username}`
                            : new Date(invite.expires_at) < new Date()
                            ? 'Expired'
                            : 'Pending'}
                          , Expires: {new Date(invite.expires_at).toLocaleString()}
                        </span>
                      </div>
                      <button
                        className="flex items-center gap-1 p-2 rounded-lg hover:bg-[var(--bg-hover)] transition text-[var(--text-primary)]"
                        onClick={() => copyToClipboard(invite.invite_url, 'Invite link')}
                        title="Copy invite link"
                      >
                        <FaCopy /> Copy
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[var(--text-secondary)]">No invites found</p>
              )}
            </div>
          )}
        </div>

        {/* Users Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">Users</h3>
          {users.length > 0 ? (
            <div className="bg-[var(--bg-secondary)] rounded-lg p-2 sm:p-4">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="w-full text-sm text-[var(--text-primary)]">
                  <thead>
                    <tr className="text-left text-[var(--text-secondary)]">
                      <th className="py-3 px-4">ID</th>
                      <th className="py-3 px-4">Username</th>
                      <th className="py-3 px-4">Display Name</th>
                      <th className="py-3 px-4">Admin</th>
                      <th className="py-3 px-4">Idea Count</th>
                      <th className="py-3 px-4">Storage Used</th>
                      <th className="py-3 px-4">AI Messages</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className={`border-t border-[var(--border-color)] hover:bg-[var(--bg-hover)] ${
                          !user.is_active ? 'line-through italic text-gray-500 dark:text-gray-400' : ''
                        }`}
                      >
                        <td className="py-3 px-4">{user.id}</td>
                        <td className="py-3 px-4">{user.username}</td>
                        <td className="py-3 px-4">{user.display_name || '-'}</td>
                        <td className="py-3 px-4">{user.is_admin ? 'Yes' : 'No'}</td>
                        <td className="py-3 px-4">
                          {user.idea_count} / {user.idea_limit ?? 'Unlimited'}
                        </td>
                        <td className="py-3 px-4">
                          {user.storage_used_mb.toFixed(2)} MB / {user.storage_limit_mb ?? 'Unlimited'}
                        </td>
                        <td className="py-3 px-4">
                          {user.ai_message_count} / {user.ai_message_limit ?? 'Unlimited'}
                        </td>
                        <td className="py-3 px-4 flex space-x-2">
                          <button
                            className="flex items-center gap-1 p-2 rounded-lg hover:bg-[var(--bg-hover)] transition text-[var(--primary)]"
                            onClick={() => handleResetPassword(user.id)}
                            title="Reset Password"
                          >
                            <FaKey /> Reset
                          </button>
                          <button
                            className="flex items-center gap-1 p-2 rounded-lg hover:bg-[var(--bg-hover)] transition text-[var(--primary)]"
                            onClick={() => handleEditUser(user)}
                            title="Edit Settings"
                          >
                            <FaEdit /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={`bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4 ${
                      !user.is_active ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[var(--primary)]/10 rounded-full flex items-center justify-center">
                          <span className="text-[var(--primary)] font-semibold text-sm">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h4 className={`font-semibold text-[var(--text-primary)] ${!user.is_active ? 'line-through' : ''}`}>
                            {user.username}
                          </h4>
                          {user.display_name && (
                            <p className="text-sm text-[var(--text-secondary)]">{user.display_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {user.is_admin && (
                          <span className="px-2 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs rounded-full">
                            Admin
                          </span>
                        )}
                        {!user.is_active && (
                          <span className="px-2 py-1 bg-gray-500/20 text-gray-500 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <span className="text-[var(--text-secondary)]">ID:</span>
                        <span className="ml-1 text-[var(--text-primary)]">{user.id}</span>
                      </div>
                      <div>
                        <span className="text-[var(--text-secondary)]">Ideas:</span>
                        <span className="ml-1 text-[var(--text-primary)]">
                          {user.idea_count}/{user.idea_limit ?? '∞'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--text-secondary)]">Storage:</span>
                        <span className="ml-1 text-[var(--text-primary)]">
                          {user.storage_used_mb.toFixed(1)}MB/{user.storage_limit_mb ?? '∞'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[var(--text-secondary)]">AI Msgs:</span>
                        <span className="ml-1 text-[var(--text-primary)]">
                          {user.ai_message_count}/{user.ai_message_limit ?? '∞'}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        className="flex-1 flex items-center justify-center gap-2 p-2 bg-[var(--primary)]/10 text-[var(--primary)] rounded-lg hover:bg-[var(--primary)]/20 transition text-sm"
                        onClick={() => handleResetPassword(user.id)}
                      >
                        <FaKey className="text-xs" /> Reset Password
                      </button>
                      <button
                        className="flex-1 flex items-center justify-center gap-2 p-2 bg-[var(--primary)]/10 text-[var(--primary)] rounded-lg hover:bg-[var(--primary)]/20 transition text-sm"
                        onClick={() => handleEditUser(user)}
                      >
                        <FaEdit className="text-xs" /> Edit Settings
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[var(--text-secondary)]">No users found</p>
          )}
        </div>

        {/* Invite Popup */}
        {showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-secondary)] p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">
                New Invite Link
              </h3>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  className="flex-1 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] text-sm"
                  value={inviteLink || 'Generating link...'}
                  readOnly
                />
                <button
                  className="flex items-center justify-center gap-1 p-2 rounded-lg hover:bg-[var(--bg-hover)] transition text-[var(--text-primary)] sm:flex-shrink-0"
                  onClick={() => copyToClipboard(inviteLink, 'Invite link')}
                  disabled={!inviteLink}
                  title="Copy invite link"
                >
                  <FaCopy /> Copy
                </button>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-hover)] transition"
                  onClick={() => {
                    setShowPopup(false);
                    setInviteLink('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Popup */}
        {resetPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-secondary)] p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">
                Reset Password Link
              </h3>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  className="flex-1 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] text-sm"
                  value={resetLink || 'Generating link...'}
                  readOnly
                />
                <button
                  className="flex items-center justify-center gap-1 p-2 rounded-lg hover:bg-[var(--bg-hover)] transition text-[var(--text-primary)] sm:flex-shrink-0"
                  onClick={() => copyToClipboard(resetLink, 'Reset link')}
                  disabled={!resetLink}
                  title="Copy reset link"
                >
                  <FaCopy /> Copy
                </button>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-hover)] transition"
                  onClick={() => {
                    setResetPopup(false);
                    setResetLink('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Settings Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-secondary)] p-4 sm:p-6 rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">
                Edit User: {editingUser.username}
              </h3>
              <div className="space-y-4">
                {/* Account Status Section */}
                <div className="border-b border-[var(--border-color)] pb-4">
                  <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">
                    Account Status
                  </h4>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                      <input
                        type="checkbox"
                        checked={userForm.is_admin}
                        onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                        className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary-light)] border-[var(--border-color)] rounded"
                      />
                      Admin Privileges
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                      <input
                        type="checkbox"
                        checked={userForm.is_active}
                        onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                        className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary-light)] border-[var(--border-color)] rounded"
                        disabled={editingUser.is_admin}
                      />
                      Active Account
                    </label>
                  </div>
                </div>

                {/* General Limits Section */}
                <div className="border-b border-[var(--border-color)] pb-4">
                  <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">
                    General Limits
                  </h4>
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <label className="flex-1 text-sm text-[var(--text-primary)]">
                        Idea Limit
                        <span className="block text-[var(--text-secondary)] text-xs">
                          Current: {editingUser.idea_count}, blank for unlimited
                        </span>
                      </label>
                      <input
                        type="number"
                        value={userForm.idea_limit ?? ''}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            idea_limit: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="w-full sm:w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                        placeholder="Unlimited"
                        min="0"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <label className="flex-1 text-sm text-[var(--text-primary)]">
                        Storage Limit (MB)
                        <span className="block text-[var(--text-secondary)] text-xs">
                          Current: {editingUser.storage_used_mb.toFixed(2)} MB, blank for unlimited
                        </span>
                      </label>
                      <input
                        type="number"
                        value={userForm.storage_limit_mb ?? ''}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            storage_limit_mb: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="w-full sm:w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                        placeholder="Unlimited"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* AI Message Limits Section */}
                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">
                      AI Message Limits
                    </h4>
                    <button
                      className="flex items-center gap-1 p-2 rounded-full hover:bg-[var(--bg-hover)] transition text-[var(--primary)]"
                      onClick={() => handleResetAIUsage(editingUser.id)}
                      title="Reset AI Usage"
                    >
                      <FaUndo className="text-sm" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <label className="flex-1 text-sm text-[var(--text-primary)]">
                        Overall AI Message Limit
                        <span className="block text-[var(--text-secondary)] text-xs">
                          Current: {editingUser.ai_message_count}, blank for unlimited
                        </span>
                      </label>
                      <input
                        type="number"
                        value={userForm.ai_message_limit ?? ''}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            ai_message_limit: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="w-full sm:w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                        placeholder="Unlimited"
                        min="0"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                      <input
                        type="checkbox"
                        checked={userForm.enforce_ai_sublimits}
                        onChange={(e) =>
                          setUserForm({ ...userForm, enforce_ai_sublimits: e.target.checked })
                        }
                        className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary-light)] border-[var(--border-color)] rounded"
                      />
                      Enforce AI Type-Specific Limits
                    </label>
                    {userForm.enforce_ai_sublimits && (
                      <div className="space-y-3 mt-2 pl-4 border-l-2 border-[var(--border-color)]">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="flex-1 text-sm text-[var(--text-primary)]">
                            Creation AI Limit
                            <span className="block text-[var(--text-secondary)] text-xs">
                              Current: {editingUser.creation_ai_count}
                            </span>
                          </label>
                          <input
                            type="number"
                            value={userForm.creation_ai_limit ?? ''}
                            onChange={(e) =>
                              setUserForm({
                                ...userForm,
                                creation_ai_limit: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            className="w-full sm:w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                            placeholder="Unlimited"
                            min="0"
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="flex-1 text-sm text-[var(--text-primary)]">
                            Chat AI Limit
                            <span className="block text-[var(--text-secondary)] text-xs">
                              Current: {editingUser.chat_ai_count}
                            </span>
                          </label>
                          <input
                            type="number"
                            value={userForm.chat_ai_limit ?? ''}
                            onChange={(e) =>
                              setUserForm({
                                ...userForm,
                                chat_ai_limit: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            className="w-full sm:w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                            placeholder="Unlimited"
                            min="0"
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="flex-1 text-sm text-[var(--text-primary)]">
                            Project AI Limit
                            <span className="block text-[var(--text-secondary)] text-xs">
                              Current: {editingUser.project_ai_count}
                            </span>
                          </label>
                          <input
                            type="number"
                            value={userForm.project_ai_limit ?? ''}
                            onChange={(e) =>
                              setUserForm({
                                ...userForm,
                                project_ai_limit: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            className="w-32 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-light)] placeholder-[var(--text-secondary)]"
                            placeholder="Unlimited"
                            min="0"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                  onClick={handleUpdateUser}
                >
                  <FiSave /> Save Changes
                </button>
                <button
                  className="px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-hover)] transition"
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;